import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { getIdToken } from 'firebase/auth';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, redirect } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { toast } from 'sonner';

import { Toaster } from '@cold-ambulance/shadcn';
import { client, createMutationErrorHandler } from '@cold-ambulance/ui';
import { MUST_CHANGE_PASSWORD_CLAIM } from '@cold-ambulance/isomorphic';

import { auth, getIdentity } from './app/firebase.tsx';
import i18n from './i18n/i18n';
import Layout from './app/layout.tsx';
import AdminsPage from './app/routes/admins/list-admin.tsx';
import ChangePassword from './app/routes/change-password.tsx';
import CustomersPage from './app/routes/customers/list-customer.tsx';
import DocumentReviewsPage from './app/routes/document-reviews/list-document-reviews.tsx';
import Home from './app/routes/home.tsx';
import SettingsPage from './app/routes/settings/settings.tsx';
import SignIn from './app/routes/signin.tsx';
import TTReviewsPage from './app/routes/tt-reviews/list-tt-reviews.tsx';
import RequestsPage from './app/routes/requests/list-request.tsx';
import VendorsPage from './app/routes/vendors/list-vendor.tsx';

async function readMustChangePassword(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  const tokenResult = await user.getIdTokenResult();
  return Boolean(tokenResult.claims[MUST_CHANGE_PASSWORD_CLAIM]);
}

async function requiresAuth() {
  const identity = await getIdentity();
  if (!identity) {
    throw redirect('/signin');
  }

  if (await readMustChangePassword()) {
    throw redirect('/change-password');
  }

  return identity;
}

async function requiresPasswordChange() {
  const identity = await getIdentity();
  if (!identity) {
    throw redirect('/signin');
  }
  if (!(await readMustChangePassword())) {
    throw redirect('/');
  }
  return identity;
}

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: createMutationErrorHandler({
      i18n,
      fallbackMessage: 'Something went wrong',
      onUnauthorized: () => {
        toast.error('You are not authorized to perform this action');
        window.location.href = '/';
      },
    }),
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      console.error(error);
    },
  }),
});

const router = createBrowserRouter([
  {
    path: '/signin',
    Component: SignIn,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
  {
    path: '/change-password',
    Component: ChangePassword,
    loader: requiresPasswordChange,
  },
  {
    Component: Layout,
    loader: requiresAuth,
    children: [
      {
        path: '/',
        index: true,
        Component: Home,
      },
      {
        path: '/admins',
        Component: AdminsPage,
      },
      {
        path: '/vendors',
        Component: VendorsPage,
      },
      {
        path: '/customers',
        Component: CustomersPage,
      },
      {
        path: '/requests',
        Component: RequestsPage,
      },
      {
        path: '/document-reviews',
        Component: DocumentReviewsPage,
      },
      {
        path: '/tt-reviews',
        Component: TTReviewsPage,
      },
      {
        path: '/settings',
        Component: SettingsPage,
      },
    ],
  },
]);

client.setOptions({
  token: () => {
    if (auth.currentUser) {
      return getIdToken(auth.currentUser);
    }
    return '';
  },
});

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <Toaster />
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
